import { useState, useEffect, useRef, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate, useLocation, useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Star, Trophy, Sparkles, ArrowRight, X, ShoppingCart, Filter, Download, Share2, Copy, Check, CheckCircle2, MessageCircle, Gift, Mail } from "lucide-react";
import confetti from "canvas-confetti";
import html2canvas from "html2canvas";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Curated Collections - Hardcoded
const COLLECTIONS = [
  {
    id: "kids-under-5",
    name: "Gifts for Kids Under 5 Years Old",
    description: "Perfect toys and gifts for toddlers and preschoolers",
    slug: "kids-under-5",
    productIds: [
      "hasbro-jenga",
      "fidget-spinners-4pack",
      "mini-voice-changer",
      "pindoio-skill-game",
      "hasbro-simon",
      "stomp-rocket-glow",
      "zipstring-classic",
      "pop-up-bluey-game",
      "magnet-cube-2x2",
      "magic-8-ball",
      "led-hover-soccer",
      "shape-shifting-box",
      "crayola-light-board",
      "crayola-bluey-stamper",
      "flybar-pogo-trick",
      "flat2fast-stunt-racer",
      "holding-hands-socks-kids",
      "thors-dart-hammer",
      "lite-brite-pokemon",
      "magnetic-blocks-150",
      "boomerang-kids",
      "electric-screwdriver-building",
      "ninja-silent-basketball",
      "indoor-basketball-hoop",
      "children-digital-camera",
      "rainbow-scratch-paper",
      "magic-rainbow-puzzle",
      "paint-sticker-under-sea",
      "pretend-play-purrse",
      "kids-walkie-talkie",
      "wireless-karaoke-mic",
      "labyrinth-wooden-maze",
      "fuse-beads-kit-24000",
      "kids-bluetooth-headphones",
      "switch-wireless-controller",
      "wheres-waldo-boxed-set",
      "how-to-draw-anything",
      "etch-a-sketch-classic",
      "lego-classic-large-box",
      "pokemon-smartwatch-kids",
      "pikachu-plush-8inch",
      "kids-toy-item-32"
    ],
    featured: true
  }
];

// Landing Page
const Landing = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen landing-bg">
      <div className="container mx-auto px-4 py-16">
        <div className="text-center mb-16 animate-fade-in">
          <div className="inline-flex items-center gap-4 mb-6">
            <img 
              src="https://customer-assets.emergentagent.com/job_giftvote/artifacts/rm2nk4xs_image.png" 
              alt="GiftDuel Logo" 
              className="giftduel-logo"
            />
            <h1 className="text-6xl font-bold text-slate-800" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Gift<span className="gradient-text">Duel</span>
            </h1>
          </div>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto" style={{ fontFamily: 'Inter, sans-serif' }}>
            Turn gift shopping into a game! Create your wishlist, battle your favorites, and discover what you really want.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8 mb-16 max-w-5xl mx-auto">
          <div 
            className="step-card step-card-clickable" 
            data-testid="step-pick"
            onClick={() => navigate('/catalog')}
            role="button"
            tabIndex={0}
            onKeyPress={(e) => e.key === 'Enter' && navigate('/catalog')}
          >
            <div className="step-number">1</div>
            <h3 className="text-2xl font-bold text-slate-800 mb-3" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Pick</h3>
            <p className="text-slate-600" style={{ fontFamily: 'Inter, sans-serif' }}>Browse our gift catalog and add your favorite items to your wishlist</p>
          </div>

          <div className="step-card" data-testid="step-play">
            <div className="step-number">2</div>
            <h3 className="text-2xl font-bold text-slate-800 mb-3" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Play</h3>
            <p className="text-slate-600" style={{ fontFamily: 'Inter, sans-serif' }}>Swipe or click your favorites in head-to-head matchups</p>
          </div>

          <div className="step-card" data-testid="step-reveal">
            <div className="step-number">3</div>
            <h3 className="text-2xl font-bold text-slate-800 mb-3" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Reveal</h3>
            <p className="text-slate-600" style={{ fontFamily: 'Inter, sans-serif' }}>Discover your ultimate gift choice and your personalized rankings</p>
          </div>
        </div>

        <div className="text-center">
          <div className="flex flex-col items-center gap-4">
            <Button 
              onClick={() => navigate('/catalog')} 
              className="cta-button cta-button-large"
              data-testid="start-shopping-btn"
            >
              Start Shopping <ArrowRight className="ml-2 w-6 h-6" />
            </Button>
            <Button 
              onClick={() => navigate('/collections')} 
              className="cta-button cta-button-large"
            >
              <Gift className="mr-2 w-6 h-6" /> Browse Curated Collections
            </Button>
          </div>
        </div>

        {/* Footer with legal links */}
        <footer className="mt-16 pt-8 border-t border-slate-200">
          <div className="flex flex-wrap justify-center items-center gap-4 text-sm text-slate-600" style={{ fontFamily: 'Inter, sans-serif' }}>
            <button 
              onClick={() => navigate('/privacy')} 
              className="hover:text-teal-600 transition-colors"
            >
              Privacy Policy
            </button>
            <span className="text-slate-300">|</span>
            <button 
              onClick={() => navigate('/terms')} 
              className="hover:text-teal-600 transition-colors"
            >
              Terms of Service
            </button>
            <span className="text-slate-300">|</span>
            <button 
              onClick={() => navigate('/affiliate')} 
              className="hover:text-teal-600 transition-colors"
            >
              Affiliate Disclosure
            </button>
          </div>
          <p className="text-center text-xs text-slate-400 mt-4" style={{ fontFamily: 'Inter, sans-serif' }}>
            © 2025 GiftDuel. All rights reserved.
          </p>
        </footer>
      </div>
    </div>
  );
};

// Collection Detail Page (Filtered Catalog)
const CollectionDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [gifts, setGifts] = useState([]);
  const [selectedGifts, setSelectedGifts] = useState([]);
  
  const collection = COLLECTIONS.find(c => c.slug === slug);
  
  useEffect(() => {
    const fetchGifts = async () => {
      try {
        const response = await axios.get(`${API}/gifts`);
        // Filter to only show products in this collection
        const filteredGifts = response.data.filter(gift => 
          collection?.productIds.includes(gift.id)
        );
        setGifts(filteredGifts);
      } catch (error) {
        console.error('Error fetching gifts:', error);
      }
    };
    fetchGifts();
  }, [collection]);

  const toggleGift = (gift) => {
    setSelectedGifts(prev => 
      prev.includes(gift.id)
        ? prev.filter(id => id !== gift.id)
        : [...prev, gift.id]
    );
  };

  const startTournament = () => {
    const sessionId = `session_${Date.now()}`;
    navigate('/my-list', { state: { sessionId, selectedGifts } });
  };

  if (!collection) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-slate-800 mb-4">Collection not found</h2>
          <Button onClick={() => navigate('/collections')}>Back to Collections</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen catalog-bg">
      <div className="container mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-4">
          <div>
            <Button 
              onClick={() => navigate('/collections')} 
              variant="outline" 
              className="mb-4 border-slate-300 text-slate-700 hover:bg-slate-100"
            >
              ← Back to Collections
            </Button>
            <h1 className="text-4xl font-bold text-slate-800 mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              {collection.name}
            </h1>
            <p className="text-slate-600" style={{ fontFamily: 'Inter, sans-serif' }}>
              {collection.description} • {gifts.length} items
            </p>
          </div>
        </div>

        {selectedGifts.length > 0 && (
          <div className="fixed bottom-6 right-6 z-50">
            <Button 
              onClick={startTournament}
              size="lg"
              className={`shadow-2xl ${selectedGifts.length >= 8 ? 'cta-button' : 'bg-slate-400 text-white cursor-not-allowed'}`}
              disabled={selectedGifts.length < 8}
            >
              View My List ({selectedGifts.length}) <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
        )}

        <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
          {gifts.map(gift => {
            const isSelected = selectedGifts.includes(gift.id);
            return (
              <Card 
                key={gift.id} 
                className={`gift-card ${isSelected ? 'gift-card-selected' : ''} cursor-pointer`}
                onClick={(e) => toggleGift(gift)}
                data-testid={`gift-card-${gift.id}`}
              >
                <div className="relative">
                  <img src={gift.image_url} alt={gift.title} className="w-full h-32 lg:h-48 object-cover rounded-t-lg" />
                  <Badge className="absolute top-1 right-1 lg:top-2 lg:right-2 bg-black/70 text-white text-xs">
                    {gift.category}
                  </Badge>
                  {isSelected && (
                    <div className="absolute inset-0 selected-overlay flex items-center justify-center rounded-t-lg">
                      <div className="selected-checkmark">
                        <CheckCircle2 className="w-10 lg:w-16 h-10 lg:h-16 text-white drop-shadow-lg" />
                      </div>
                    </div>
                  )}
                </div>
                <div className="p-2 lg:p-4">
                  <h3 className="font-bold text-sm lg:text-lg mb-1 lg:mb-2 text-slate-800 line-clamp-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                    {gift.title}
                  </h3>
                  <p className="text-xs lg:text-sm text-slate-600 mb-2 lg:mb-3 line-clamp-2 hidden lg:block" style={{ fontFamily: 'Inter, sans-serif' }}>
                    {gift.description}
                  </p>
                  <div className="flex items-center justify-between mb-2 lg:mb-3">
                    <span className="text-sm lg:text-xl font-bold price-text">${gift.price.toFixed(2)}</span>
                    <div className="flex items-center gap-1">
                      <Star className="w-3 h-3 lg:w-4 lg:h-4 fill-amber-500 text-amber-500" />
                      <span className="text-xs lg:text-sm text-slate-600">{gift.rating}</span>
                    </div>
                  </div>
                  <Button 
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleGift(gift);
                    }}
                    className={`${isSelected ? 'select-button-active w-full' : 'select-button w-full'} text-xs lg:text-sm py-1 lg:py-2 mb-2`}
                    data-testid={`select-gift-${gift.id}`}
                  >
                    {isSelected ? (
                      <>
                        <span className="hidden lg:inline">Selected</span>
                        <span className="lg:hidden">Selected</span>
                      </>
                    ) : (
                      <>
                        <span className="hidden lg:inline">Add to List</span>
                        <span className="lg:hidden">Add</span>
                      </>
                    )}
                  </Button>
                  <Button
                    onClick={(e) => {
                      e.stopPropagation();
                      window.open(gift.affiliate_link, '_blank');
                    }}
                    variant="outline"
                    className="w-full text-xs lg:text-sm py-1 lg:py-2 border-slate-300"
                    data-testid={`view-gift-${gift.id}`}
                  >
                    <span className="hidden lg:inline">View on Amazon</span>
                    <span className="lg:hidden">View</span>
                  </Button>
                </div>
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// Gift Catalog Page with Filtering & Sorting
const Catalog = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [gifts, setGifts] = useState([]);
  const [filteredGifts, setFilteredGifts] = useState([]);
  const [selectedGifts, setSelectedGifts] = useState([]);
  const [sessionId] = useState(() => `session-${Date.now()}`);
  
  // Filter states
  const [maxPrice, setMaxPrice] = useState([500]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [sortBy, setSortBy] = useState('none');
  
  // Onboarding states
  const [pulsingCards, setPulsingCards] = useState([]);
  const [milestoneMessage, setMilestoneMessage] = useState('');
  const [showWelcomePopup, setShowWelcomePopup] = useState(true);

  const fetchGifts = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/gifts`);
      setGifts(response.data);
      
      // Set random pulsing cards for empty state
      const randomIndices = [];
      while (randomIndices.length < 3 && randomIndices.length < response.data.length) {
        const idx = Math.floor(Math.random() * Math.min(12, response.data.length));
        if (!randomIndices.includes(idx)) {
          randomIndices.push(response.data[idx].id);
        }
      }
      setPulsingCards(randomIndices);
    } catch (e) {
      console.error("Error fetching gifts:", e);
    }
  }, []);

  const applyFiltersAndSort = useCallback(() => {
    let filtered = [...gifts];

    // Filter by max price
    filtered = filtered.filter(g => g.price <= maxPrice[0]);

    // Filter by categories
    if (selectedCategories.length > 0) {
      filtered = filtered.filter(g => selectedCategories.includes(g.category));
    }

    // Sort
    if (sortBy === 'price-asc') {
      filtered.sort((a, b) => a.price - b.price);
    } else if (sortBy === 'price-desc') {
      filtered.sort((a, b) => b.price - a.price);
    } else if (sortBy === 'rating') {
      filtered.sort((a, b) => b.rating - a.rating);
    } else if (sortBy === 'name') {
      filtered.sort((a, b) => a.title.localeCompare(b.title));
    }

    setFilteredGifts(filtered);
  }, [gifts, maxPrice, selectedCategories, sortBy, setFilteredGifts]);

  const toggleCategory = (category) => {
    setSelectedCategories(prev => 
      prev.includes(category) 
        ? prev.filter(c => c !== category)
        : [...prev, category]
    );
  };

  const toggleGift = (giftId, e) => {
    // Prevent toggling if clicking on the button itself (button has its own handler)
    if (e && e.target.closest('button')) {
      return;
    }
    
    setSelectedGifts(prev => {
      const newSelection = prev.includes(giftId) 
        ? prev.filter(id => id !== giftId)
        : [...prev, giftId];
      
      // Show milestone messages
      if (!prev.includes(giftId)) {
        // Adding a gift
        if (newSelection.length === 2) {
          showMilestone("Great start! 🎉");
        } else if (newSelection.length === 4) {
          showMilestone("You're doing great! Pick a few more 🌟");
        } else if (newSelection.length === 6) {
          showMilestone("Fantastic! Almost there!");
        } else if (newSelection.length === 8) {
          showMilestone("Amazing selection! 🏆");
        }
      }
      
      return newSelection;
    });
  };
  
  const showMilestone = (message) => {
    setMilestoneMessage(message);
    setTimeout(() => {
      setMilestoneMessage('');
    }, 2000);
  };

  const goToMyList = () => {
    navigate('/my-list', { state: { sessionId, selectedGifts } });
  };

  const resetFilters = () => {
    setMaxPrice([500]);
    setSelectedCategories([]);
    setSortBy('none');
  };

  useEffect(() => {
    fetchGifts();
    
    // Check if we're coming back from My List with existing selections
    if (location?.state?.existingSelections) {
      setSelectedGifts(location.state.existingSelections);
      setShowWelcomePopup(false); // Don't show welcome popup when coming back
    }
  }, [fetchGifts, location?.state?.existingSelections]);

  useEffect(() => {
    applyFiltersAndSort();
  }, [gifts, maxPrice, selectedCategories, sortBy, applyFiltersAndSort]);

  const categories = ['Tech', 'Home', 'Fashion', 'Fitness', 'Entertainment', 'Food'];

  return (
    <div className="min-h-screen catalog-bg">
      {/* Welcome Popup */}
      {showWelcomePopup && (
        <div className="onboarding-overlay">
          <div className="onboarding-content">
            <div className="text-center">
              <h2 className="text-4xl font-bold text-slate-800 mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Welcome! 🎁
              </h2>
              
              <p className="text-xl text-slate-700 mb-6" style={{ fontFamily: 'Inter, sans-serif' }}>
                Browse the gifts below and tap <strong>&quot;Add to List&quot;</strong> on items you like. Select any <strong>even number</strong> of gifts (8, 10, 12, etc.) to start your tournament!
              </p>
              
              <Button
                onClick={() => setShowWelcomePopup(false)}
                size="lg"
                className="cta-button"
              >
                Let&apos;s Go! <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      )}
      
      {/* Selection Counter */}
      {selectedGifts.length > 0 && (
        <div className="selection-counter">
          {selectedGifts.length} {selectedGifts.length === 1 ? 'gift' : 'gifts'} selected
        </div>
      )}
      
      {/* Milestone Message */}
      {milestoneMessage && (
        <div className="milestone-message">
          {milestoneMessage}
        </div>
      )}
      
      <div className="container mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-4">
          <div>
            <p className="text-slate-600" style={{ fontFamily: 'Inter, sans-serif' }}>
              {filteredGifts.length} gift{filteredGifts.length !== 1 ? 's' : ''} available
            </p>
          </div>
          <Button onClick={() => navigate('/')} variant="outline" className="border-slate-300 text-slate-700 hover:bg-slate-100">
            Back to Home
          </Button>
        </div>

        {/* Amazon Associate Disclosure */}
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 mb-6">
          <p className="text-sm text-slate-600 text-center" style={{ fontFamily: 'Inter, sans-serif' }}>
            <span className="font-semibold">As an Amazon Associate, GiftDuel earns from qualifying purchases.</span> This commission doesn&apos;t cost you anything extra — we simply receive a small referral fee when you use our links. Prices may vary slightly if the seller adjusts them.
          </p>
        </div>

        {/* Filters Section - Always Visible */}
        <div className="filter-panel mb-6" data-testid="filters-panel">
          <div className="grid md:grid-cols-4 gap-6 items-end">
            {/* Max Price */}
            <div>
              <label className="text-slate-700 font-semibold mb-3 block text-sm" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Max Price: ${maxPrice[0]}
              </label>
              <Slider
                value={maxPrice}
                onValueChange={setMaxPrice}
                max={500}
                min={0}
                step={10}
                className="mb-2"
                data-testid="price-slider"
              />
              <div className="flex justify-between text-xs text-slate-500 mt-1">
                <span>$0</span>
                <span>$500</span>
              </div>
            </div>

            {/* Categories */}
            <div className="md:col-span-2">
              <label className="text-slate-700 font-semibold mb-3 block text-sm" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Categories
              </label>
              <div className="flex flex-wrap gap-2">
                {categories.map(cat => (
                  <Badge
                    key={cat}
                    onClick={() => toggleCategory(cat)}
                    className={`cursor-pointer transition-colors ${
                      selectedCategories.includes(cat)
                        ? 'category-badge-active'
                        : 'category-badge'
                    }`}
                    data-testid={`category-filter-${cat}`}
                  >
                    {cat}
                  </Badge>
                ))}
              </div>
            </div>

            {/* Sort By */}
            <div>
              <label className="text-slate-700 font-semibold mb-3 block text-sm" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Sort By
              </label>
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="bg-white border-slate-200 text-slate-700" data-testid="sort-select">
                  <SelectValue placeholder="Select sorting" />
                </SelectTrigger>
                <SelectContent className="bg-white border-slate-200">
                  <SelectItem value="none" className="text-slate-700 hover:bg-slate-100">Default</SelectItem>
                  <SelectItem value="price-asc" className="text-slate-700 hover:bg-slate-100">Price: Low to High</SelectItem>
                  <SelectItem value="price-desc" className="text-slate-700 hover:bg-slate-100">Price: High to Low</SelectItem>
                  <SelectItem value="rating" className="text-slate-700 hover:bg-slate-100">Highest Rated</SelectItem>
                  <SelectItem value="name" className="text-slate-700 hover:bg-slate-100">Name (A-Z)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {(selectedCategories.length > 0 || maxPrice[0] < 500 || sortBy !== 'none') && (
            <div className="mt-4">
              <Button 
                onClick={resetFilters}
                variant="outline"
                size="sm"
                className="border-slate-300 text-slate-700 hover:bg-slate-100"
                data-testid="reset-filters-btn"
              >
                Clear All Filters
              </Button>
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 lg:gap-6 mb-24">
          {filteredGifts.map(gift => {
            const isSelected = selectedGifts.includes(gift.id);
            const shouldPulse = selectedGifts.length === 0 && pulsingCards.includes(gift.id);
            return (
              <Card 
                key={gift.id} 
                className={`gift-card ${isSelected ? 'gift-card-selected' : ''} ${shouldPulse ? 'pulse-card' : ''} cursor-pointer`}
                onClick={(e) => toggleGift(gift.id, e)}
                data-testid={`gift-card-${gift.id}`}
              >
                <div className="relative">
                  <img src={gift.image_url} alt={gift.title} className="w-full h-32 lg:h-48 object-cover rounded-t-lg" />
                  <Badge className="absolute top-1 right-1 lg:top-2 lg:right-2 bg-black/70 text-white text-xs">
                    {gift.category}
                  </Badge>
                  {isSelected && (
                    <div className="absolute inset-0 selected-overlay flex items-center justify-center rounded-t-lg">
                      <div className="selected-checkmark">
                        <CheckCircle2 className="w-10 lg:w-16 h-10 lg:h-16 text-white drop-shadow-lg" />
                      </div>
                    </div>
                  )}
                </div>
                <div className="p-2 lg:p-4">
                  <h3 className="font-bold text-sm lg:text-lg mb-1 lg:mb-2 text-slate-800 line-clamp-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                    {gift.title}
                  </h3>
                  <p className="text-xs lg:text-sm text-slate-600 mb-2 lg:mb-3 line-clamp-2 hidden lg:block" style={{ fontFamily: 'Inter, sans-serif' }}>
                    {gift.description}
                  </p>
                  <div className="flex items-center justify-between mb-2 lg:mb-3">
                    <span className="text-sm lg:text-xl font-bold price-text">${gift.price.toFixed(2)}</span>
                    <div className="flex items-center gap-1">
                      <Star className="w-3 h-3 lg:w-4 lg:h-4 fill-amber-500 text-amber-500" />
                      <span className="text-xs lg:text-sm text-slate-600">{gift.rating}</span>
                    </div>
                  </div>
                  <Button 
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleGift(gift.id);
                    }}
                    className={`${isSelected ? 'select-button-active w-full' : 'select-button w-full'} text-xs lg:text-sm py-1 lg:py-2 mb-2`}
                    data-testid={`select-gift-${gift.id}`}
                  >
                    {isSelected ? (
                      <>
                        <span className="hidden lg:inline">Selected</span>
                        <span className="lg:hidden">Selected</span>
                      </>
                    ) : (
                      <>
                        <span className="hidden lg:inline">Add to List</span>
                        <span className="lg:hidden">Add</span>
                      </>
                    )}
                  </Button>
                  <Button
                    onClick={(e) => {
                      e.stopPropagation();
                      window.open(gift.affiliate_link, '_blank');
                    }}
                    variant="outline"
                    className="w-full text-xs lg:text-sm py-1 lg:py-2 border-slate-300"
                    data-testid={`view-gift-${gift.id}`}
                  >
                    <ShoppingCart className="w-3 h-3 lg:w-4 lg:h-4 mr-1" />
                    View on Amazon
                  </Button>
                </div>
              </Card>
            );
          })}
        </div>

        {filteredGifts.length === 0 && (
          <div className="text-center py-16">
            <p className="text-xl text-slate-500" style={{ fontFamily: 'Inter, sans-serif' }}>
              No gifts match your filters. Try adjusting your criteria.
            </p>
          </div>
        )}
      </div>

      {selectedGifts.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 selected-footer shadow-2xl" data-testid="selected-footer">
          <div className="container mx-auto flex justify-between items-center">
            <div className="text-white">
              <p className="text-lg font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                {selectedGifts.length} gift{selectedGifts.length !== 1 ? 's' : ''} selected
              </p>
              <p className="text-sm text-white/90" style={{ fontFamily: 'Inter, sans-serif' }}>
                {selectedGifts.length >= 8 ? 'Ready to start your tournament?' : 'Select at least 8 gifts to start'}
              </p>
            </div>
            <Button 
              onClick={goToMyList} 
              size="lg" 
              className={selectedGifts.length >= 8 ? 'cta-button' : 'bg-slate-400 text-white cursor-not-allowed'}
              disabled={selectedGifts.length < 8}
              data-testid="view-my-list-btn"
            >
              View My List <ArrowRight className="ml-2" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

// My List Page
const MyList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [gifts, setGifts] = useState([]);
  const [selectedGifts, setSelectedGifts] = useState([]);
  const [sessionId, setSessionId] = useState('');

  const fetchGiftDetails = useCallback(async (giftIds) => {
    try {
      const response = await axios.get(`${API}/gifts`);
      const filtered = response.data.filter(g => giftIds.includes(g.id));
      setGifts(filtered);
    } catch (e) {
      console.error("Error fetching gift details:", e);
    }
  }, [setGifts]);

  useEffect(() => {
    const state = location?.state;
    if (state) {
      setSessionId(state.sessionId);
      setSelectedGifts(state.selectedGifts);
      fetchGiftDetails(state.selectedGifts);
    }
  }, [location?.state, fetchGiftDetails]);

  const removeGift = (giftId) => {
    const updated = selectedGifts.filter(id => id !== giftId);
    setSelectedGifts(updated);
    setGifts(gifts.filter(g => g.id !== giftId));
  };

  const startTournament = async () => {
    const count = selectedGifts.length;
    
    if (count < 8) {
      alert('Please select at least 8 gifts to start the tournament');
      return;
    }
    
    // Check if even number
    if (count % 2 !== 0) {
      alert(`Please select one more gift to have an even number! You currently have ${count} gifts selected.`);
      return;
    }

    try {
      await axios.post(`${API}/user-list`, {
        session_id: sessionId,
        gift_ids: selectedGifts
      });

      await axios.post(`${API}/tournament/create`, {
        session_id: sessionId,
        gift_ids: selectedGifts
      });

      navigate('/bracket', { state: { sessionId } });
    } catch (e) {
      console.error("Error creating tournament:", e);
    }
  };

  return (
    <div className="min-h-screen catalog-bg">
      <div className="container mx-auto px-4 py-8">
        {/* Centered action buttons */}
        <div className="flex flex-col items-center mb-12">
          <div className="flex gap-4 mb-6">
            <Button onClick={() => navigate('/catalog', { state: { existingSelections: selectedGifts } })} variant="outline" className="border-slate-300 text-slate-700 hover:bg-slate-100">
              Add More Gifts
            </Button>
            <Button onClick={() => navigate('/')} variant="outline" className="border-slate-300 text-slate-700 hover:bg-slate-100">
              Back to Home
            </Button>
          </div>
          
          {gifts.length >= 8 && gifts.length % 2 === 0 ? (
            <Button onClick={startTournament} size="lg" className="tournament-start-button" data-testid="start-tournament-btn">
              <img 
                src="https://customer-assets.emergentagent.com/job_giftduel-admin/artifacts/297n31pv_image.png" 
                alt="GiftDuel" 
                className="mr-3 w-8 h-8"
              /> 
              <span className="text-2xl">Start Tournament</span>
            </Button>
          ) : gifts.length >= 8 && gifts.length % 2 !== 0 ? (
            <div className="text-center">
              <p className="text-lg text-amber-600 mb-2 font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                ⚠️ Almost ready!
              </p>
              <p className="text-xl text-slate-700 mb-2" style={{ fontFamily: 'Inter, sans-serif' }}>
                Select <strong>1 more gift</strong> to have an even number
              </p>
              <p className="text-2xl font-bold text-amber-600" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                {gifts.length} gifts (need even number)
              </p>
            </div>
          ) : (
            <div className="text-center">
              <p className="text-lg text-slate-600 mb-2" style={{ fontFamily: 'Inter, sans-serif' }}>
                Select at least 8 gifts to start
              </p>
              <p className="text-2xl font-bold text-teal-600" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                {gifts.length} / 8 gifts selected
              </p>
            </div>
          )}
          
          <p className="text-slate-500 mt-4" style={{ fontFamily: 'Inter, sans-serif' }}>
            {gifts.length} gift{gifts.length !== 1 ? 's' : ''} in your list
          </p>
        </div>

        <div className="flex flex-wrap justify-center gap-4 max-w-4xl mx-auto">
          {gifts.map(gift => (
            <div key={gift.id} className="thumbnail-card relative group" data-testid={`my-list-gift-${gift.id}`}>
              <button
                onClick={() => removeGift(gift.id)}
                className="absolute -top-2 -right-2 z-10 bg-red-500 hover:bg-red-600 text-white rounded-full p-2 shadow-lg transition-all opacity-0 group-hover:opacity-100"
                data-testid={`remove-gift-${gift.id}`}
                aria-label={`Remove ${gift.title}`}
              >
                <X className="w-3 h-3" />
              </button>
              <img 
                src={gift.image_url} 
                alt={gift.title} 
                className="w-full h-full object-cover rounded-lg" 
                title={gift.title}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Bracket/Tournament Page with Swipe Interface and Round Animations
const Bracket = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [tournament, setTournament] = useState(null);
  const [gifts, setGifts] = useState([]);
  const [sessionId, setSessionId] = useState('');
  const [currentMatchupIndex, setCurrentMatchupIndex] = useState(0);
  const [showFullBracket, setShowFullBracket] = useState(false);
  const [swipeDirection, setSwipeDirection] = useState(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const [showRoundTransition, setShowRoundTransition] = useState(false);
  const [currentRound, setCurrentRound] = useState(1);
  
  const touchStartX = useRef(0);
  const touchStartY = useRef(0);
  const containerRef = useRef(null);

  const fetchTournament = useCallback(async (sid) => {
    try {
      const response = await axios.get(`${API}/tournament/${sid}`);
      setTournament(response.data);
      setCurrentRound(response.data.current_round);
      const firstUndecided = response.data.matchups.findIndex(m => m.length === 2);
      setCurrentMatchupIndex(firstUndecided !== -1 ? firstUndecided : 0);
    } catch (e) {
      console.error("Error fetching tournament:", e);
    }
  }, [setTournament, setCurrentRound, setCurrentMatchupIndex, setShowRoundTransition]);

  const fetchGifts = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/gifts`);
      setGifts(response.data);
    } catch (e) {
      console.error("Error fetching gifts:", e);
    }
  }, [setGifts]);

  useEffect(() => {
    const state = location?.state;
    if (state) {
      setSessionId(state.sessionId);
      fetchTournament(state.sessionId);
      fetchGifts();
    }
  }, [location?.state, fetchTournament, fetchGifts]);

  useEffect(() => {
    if (tournament && tournament.current_round !== currentRound) {
      // New round started
      setShowRoundTransition(true);
      setCurrentRound(tournament.current_round);
      
      // Hide transition after 2 seconds
      setTimeout(() => {
        setShowRoundTransition(false);
      }, 2000);
    }
  }, [tournament, currentRound]);

  const selectWinner = async (winnerId, direction = null) => {
    if (isAnimating) return;
    
    setIsAnimating(true);
    
    // Determine which gift is the loser and set flick direction
    const currentMatchup = tournament.matchups[currentMatchupIndex];
    const loserId = currentMatchup.find(id => id !== winnerId);
    
    // Set direction based on winner position if not provided
    if (!direction) {
      direction = currentMatchup[0] === winnerId ? 'right' : 'left';
    }
    setSwipeDirection(direction);
    
    // Wait for flick animation (500ms for fast, smooth animation)
    await new Promise(resolve => setTimeout(resolve, 500));
    
    try {
      await axios.post(`${API}/tournament/advance`, {
        session_id: sessionId,
        winner_id: winnerId,
        matchup_index: currentMatchupIndex
      });

      const response = await axios.get(`${API}/tournament/${sessionId}`);
      setTournament(response.data);

      if (response.data.winner_id) {
        navigate('/results', { state: { sessionId } });
      } else {
        const nextUndecided = response.data.matchups.findIndex(m => m.length === 2);
        setCurrentMatchupIndex(nextUndecided !== -1 ? nextUndecided : 0);
      }
    } catch (e) {
      console.error("Error advancing winner:", e);
    } finally {
      setSwipeDirection(null);
      setIsAnimating(false);
    }
  };

  const skipMatchup = async () => {
    if (isAnimating) return;
    
    setIsAnimating(true);
    
    try {
      await axios.post(`${API}/tournament/skip`, {
        session_id: sessionId,
        matchup_index: currentMatchupIndex
      });

      const response = await axios.get(`${API}/tournament/${sessionId}`);
      setTournament(response.data);

      if (response.data.winner_id) {
        navigate('/results', { state: { sessionId } });
      } else {
        const nextUndecided = response.data.matchups.findIndex(m => m.length === 2);
        setCurrentMatchupIndex(nextUndecided !== -1 ? nextUndecided : 0);
      }
    } catch (e) {
      console.error("Error skipping matchup:", e);
    } finally {
      setIsAnimating(false);
    }
  };

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
  };

  const handleTouchEnd = (e, gift1Id, gift2Id) => {
    const touchEndX = e.changedTouches[0].clientX;
    const touchEndY = e.changedTouches[0].clientY;
    
    const deltaX = touchEndX - touchStartX.current;
    const deltaY = touchEndY - touchStartY.current;
    
    // Only trigger if horizontal swipe is dominant
    if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 50) {
      if (deltaX > 0) {
        // Swipe right - select gift 1
        selectWinner(gift1Id, 'right');
      } else {
        // Swipe left - select gift 2
        selectWinner(gift2Id, 'left');
      }
    }
  };

  const getGiftById = (id) => gifts.find(g => g.id === id);

  const calculateProgress = () => {
    if (!tournament) return 0;
    const totalMatches = tournament.original_gifts.length - 1;
    const completedMatches = tournament.rankings.length;
    return Math.max(0, Math.round((completedMatches / totalMatches) * 100));
  };

  if (!tournament || gifts.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center catalog-bg">
        <div className="text-slate-800 text-xl" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Loading tournament...</div>
      </div>
    );
  }

  const currentMatchup = tournament.matchups[currentMatchupIndex];
  const progress = calculateProgress();

  // Find next undecided matchup if current one is empty
  if (!currentMatchup || currentMatchup.length < 2) {
    const nextUndecided = tournament.matchups.findIndex(m => m.length === 2);
    if (nextUndecided !== -1 && nextUndecided !== currentMatchupIndex) {
      // Found a valid matchup, update index
      setCurrentMatchupIndex(nextUndecided);
      return null; // Re-render will happen with new index
    }
    
    // No undecided matchups found, tournament might be complete
    if (tournament.winner_id) {
      navigate('/results', { state: { sessionId } });
      return null;
    }
    
    return (
      <div className="min-h-screen flex items-center justify-center catalog-bg">
        <div className="text-slate-800 text-xl" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Processing next round...</div>
      </div>
    );
  }

  const gift1 = getGiftById(currentMatchup[0]);
  const gift2 = getGiftById(currentMatchup[1]);

  return (
    <div className="min-h-screen catalog-bg">
      {/* Round Transition Overlay */}
      {showRoundTransition && (
        <div className="round-transition-overlay">
          <div className="round-transition-content">
            <img 
              src="https://customer-assets.emergentagent.com/job_giftduel-admin/artifacts/oxq5kcq7_image.png" 
              alt="GiftDuel" 
              className="w-32 h-32 mb-6 animate-bounce"
            />
            <h2 className="text-7xl font-bold text-white mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              {(() => {
                const totalGifts = tournament.original_gifts.length;
                const remainingGifts = tournament.matchups.filter(m => m.length > 0).length;
                
                // Final round (2 gifts remaining)
                if (remainingGifts === 1) {
                  return 'Final';
                }
                // Semi-final (4 gifts remaining, which means 2 matchups)
                else if (remainingGifts === 2) {
                  return 'Semi Final';
                }
                // Regular rounds
                else {
                  return `Round ${tournament.current_round}`;
                }
              })()}
            </h2>
            <p className="text-2xl text-white/90" style={{ fontFamily: 'Inter, sans-serif' }}>Get ready!</p>
          </div>
        </div>
      )}

      <div className="container mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <div className="flex flex-col items-center gap-3 mb-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_giftduel-admin/artifacts/j0a1chwo_image.png" 
              alt="GiftDuel" 
              className="w-20 h-20"
            />
            <h1 className="text-4xl font-bold text-slate-800" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              {(() => {
                const totalGifts = tournament.original_gifts.length;
                const remainingGifts = tournament.matchups.filter(m => m.length > 0).length;
                
                // Final round (2 gifts remaining)
                if (remainingGifts === 1) {
                  return 'Final';
                }
                // Semi-final (4 gifts remaining, which means 2 matchups)
                else if (remainingGifts === 2) {
                  return 'Semi Final';
                }
                // Regular rounds
                else {
                  return `Round ${tournament.current_round}`;
                }
              })()}
            </h1>
          </div>
          <p className="text-slate-600 mb-2" style={{ fontFamily: 'Inter, sans-serif' }}>
            <span className="hidden sm:inline">Choose your favorite gift or </span>
            <span className="sm:hidden">Swipe left/right or tap</span>
            <span className="hidden sm:inline">swipe on mobile</span>
          </p>
          
          {/* Progress Bar */}
          <div className="max-w-md mx-auto mb-4" data-testid="tournament-progress">
            <div className="flex justify-between text-sm text-slate-600 mb-2">
              <span>Tournament Progress</span>
              <span>{progress}%</span>
            </div>
            <div className="h-3 bg-slate-200 rounded-full overflow-hidden">
              <div 
                className="h-full progress-bar transition-all duration-500"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>

          <div className="mt-2">
            <span className="gradient-text font-bold text-lg" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Matchup {currentMatchupIndex + 1} of {tournament.matchups.length}
            </span>
          </div>

          {/* Toggle Bracket View */}
          <Button
            onClick={() => setShowFullBracket(!showFullBracket)}
            variant="outline"
            size="sm"
            className="mt-4 filter-button"
            data-testid="toggle-bracket-btn"
          >
            {showFullBracket ? 'Hide Full Bracket' : 'Show Full Bracket'}
          </Button>
        </div>

        {/* Full Bracket Visualization */}
        {showFullBracket && (
          <div className="mb-8 bracket-visualization" data-testid="full-bracket">
            <h3 className="text-2xl font-bold text-slate-800 text-center mb-6" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Tournament Bracket
            </h3>
            <div className="bracket-grid">
              {tournament.matchups.map((matchup, idx) => {
                const isDecided = matchup.length === 1;
                const isCurrent = idx === currentMatchupIndex;
                const gift = isDecided ? getGiftById(matchup[0]) : null;
                
                return (
                  <div 
                    key={idx} 
                    className={`bracket-item ${
                      isCurrent ? 'bracket-current' : isDecided ? 'bracket-decided' : 'bracket-pending'
                    }`}
                    data-testid={`bracket-matchup-${idx}`}
                  >
                    <div className="bracket-number">Match {idx + 1}</div>
                    {isDecided ? (
                      <div className="bracket-winner">
                        <img src={gift.image_url} alt={gift.title} className="w-12 h-12 rounded object-cover" />
                        <div className="ml-2">
                          <div className="text-sm font-semibold text-slate-800">{gift.title}</div>
                          <div className="text-xs price-text">${gift.price}</div>
                        </div>
                      </div>
                    ) : matchup.length === 2 ? (
                      <div className="text-sm text-slate-500">
                        {isCurrent ? 'Current Match' : 'Upcoming'}
                      </div>
                    ) : (
                      <div className="text-sm text-slate-500">Waiting...</div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div 
          className="max-w-6xl mx-auto relative"
          ref={containerRef}
          onTouchStart={handleTouchStart}
          onTouchEnd={(e) => handleTouchEnd(e, gift1.id, gift2.id)}
        >
          {/* VS Badge in the middle */}
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10 pointer-events-none">
            <div className="w-12 h-12 md:w-14 md:h-14 rounded-full bg-gradient-to-br from-teal-500 to-amber-500 shadow-lg flex items-center justify-center border-4 border-white">
              <span className="text-white font-bold text-xl md:text-3xl" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>VS</span>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 md:gap-8" data-testid="matchup-container">
            {/* Gift 1 */}
            <div 
              className={`matchup-card cursor-pointer ${
                swipeDirection === 'left' ? 'flick-out-left' : 
                swipeDirection === 'right' && currentMatchup[0] !== gift1.id ? 'flick-out-right' : ''
              }`}
              onClick={() => !isAnimating && selectWinner(gift1.id)}
              data-testid={`matchup-gift-${gift1.id}`}
            >
              <div className="relative">
                <img src={gift1.image_url} alt={gift1.title} className="w-full h-48 md:h-64 object-cover rounded-t-lg" />
                <Badge className="absolute top-2 right-2 md:top-3 md:right-3 bg-black/70 text-white text-xs md:text-sm">
                  {gift1.category}
                </Badge>
              </div>
              <div className="p-3 md:p-6">
                <h3 className="font-bold text-base md:text-2xl mb-2 md:mb-3 text-slate-800" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  {gift1.title}
                </h3>
                <p className="text-xs md:text-base text-slate-600 mb-2 md:mb-4 line-clamp-2" style={{ fontFamily: 'Inter, sans-serif' }}>
                  {gift1.description}
                </p>
                <div className="flex items-center justify-between mb-2 md:mb-4">
                  <span className="text-lg md:text-2xl font-bold price-text">${gift1.price.toFixed(2)}</span>
                  <div className="flex items-center gap-1">
                    <Star className="w-4 h-4 md:w-5 md:h-5 fill-amber-500 text-amber-500" />
                    <span className="text-sm md:text-lg text-slate-700">{gift1.rating}</span>
                  </div>
                </div>
                <div className="text-center">
                  <p className="text-xs md:text-sm text-slate-600" style={{ fontFamily: 'Inter, sans-serif' }}>
                    <span className="hidden md:inline">Click to choose or </span>
                    <span className="md:hidden">Tap</span>
                    <span className="hidden md:inline">swipe right</span>
                  </p>
                </div>
              </div>
            </div>

            {/* Gift 2 */}
            <div 
              className={`matchup-card cursor-pointer ${
                swipeDirection === 'right' ? 'flick-out-right' : 
                swipeDirection === 'left' && currentMatchup[1] !== gift2.id ? 'flick-out-left' : ''
              }`}
              onClick={() => !isAnimating && selectWinner(gift2.id)}
              data-testid={`matchup-gift-${gift2.id}`}
            >
              <div className="relative">
                <img src={gift2.image_url} alt={gift2.title} className="w-full h-48 md:h-64 object-cover rounded-t-lg" />
                <Badge className="absolute top-2 right-2 md:top-3 md:right-3 bg-black/70 text-white text-xs md:text-sm">
                  {gift2.category}
                </Badge>
              </div>
              <div className="p-3 md:p-6">
                <h3 className="font-bold text-base md:text-2xl mb-2 md:mb-3 text-slate-800" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  {gift2.title}
                </h3>
                <p className="text-xs md:text-base text-slate-600 mb-2 md:mb-4 line-clamp-2" style={{ fontFamily: 'Inter, sans-serif' }}>
                  {gift2.description}
                </p>
                <div className="flex items-center justify-between mb-2 md:mb-4">
                  <span className="text-lg md:text-2xl font-bold price-text">${gift2.price.toFixed(2)}</span>
                  <div className="flex items-center gap-1">
                    <Star className="w-4 h-4 md:w-5 md:h-5 fill-amber-500 text-amber-500" />
                    <span className="text-sm md:text-lg text-slate-700">{gift2.rating}</span>
                  </div>
                </div>
                <div className="text-center">
                  <p className="text-xs md:text-sm text-slate-600" style={{ fontFamily: 'Inter, sans-serif' }}>
                    <span className="hidden md:inline">Click to choose or </span>
                    <span className="md:hidden">Tap</span>
                    <span className="hidden md:inline">swipe left</span>
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="text-center mt-4 md:mt-8">
            <div className="text-2xl md:text-4xl font-bold text-slate-300" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>VS</div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Results Page with Enhanced Share functionality
const Results = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [tournament, setTournament] = useState(null);
  const [gifts, setGifts] = useState([]);
  const [sessionId, setSessionId] = useState('');
  const [copied, setCopied] = useState(false);
  const resultsRef = useRef(null);

  const triggerConfetti = useCallback(() => {
    const duration = 3000;
    const end = Date.now() + duration;

    const frame = () => {
      confetti({
        particleCount: 3,
        angle: 60,
        spread: 55,
        origin: { x: 0 },
        colors: ['#14B8A6', '#F59E0B', '#10B981']
      });
      confetti({
        particleCount: 3,
        angle: 120,
        spread: 55,
        origin: { x: 1 },
        colors: ['#14B8A6', '#F59E0B', '#10B981']
      });

      if (Date.now() < end) {
        requestAnimationFrame(frame);
      }
    };
    frame();
  }, []);

  const fetchTournament = useCallback(async (sid) => {
    try {
      const response = await axios.get(`${API}/tournament/${sid}`);
      setTournament(response.data);
    } catch (e) {
      console.error("Error fetching tournament:", e);
    }
  }, [setTournament]);

  const fetchGifts = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/gifts`);
      setGifts(response.data);
    } catch (e) {
      console.error("Error fetching gifts:", e);
    }
  }, [setGifts]);

  useEffect(() => {
    const state = location?.state;
    if (state) {
      setSessionId(state.sessionId);
      fetchTournament(state.sessionId);
      fetchGifts();
      triggerConfetti();
    }
  }, [location?.state, fetchTournament, fetchGifts, triggerConfetti]);

  const getGiftById = (id) => gifts.find(g => g.id === id);

  const playAgain = () => {
    navigate('/catalog');
  };

  const getShareText = () => {
    if (!tournament) return '';
    const winnerGift = getGiftById(tournament.winner_id);
    const topRankings = tournament.rankings.slice(-5).reverse().map(id => getGiftById(id)).filter(Boolean);
    
    return `🏆 My GiftDuel Results!\n\n#1 Choice: ${winnerGift.title} - $${winnerGift.price}\n\nTop 5:\n${topRankings.map((g, i) => `${i + 1}. ${g.title} - $${g.price}`).join('\n')}\n\nDiscover your perfect gift at: ${window.location.origin}`;
  };

  const copyResultsText = () => {
    const text = getShareText();
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const shareViaWhatsApp = () => {
    const text = encodeURIComponent(getShareText());
    window.open(`https://wa.me/?text=${text}`, '_blank');
  };

  const shareViaSMS = () => {
    const text = encodeURIComponent(getShareText());
    // For iOS use &body=, for Android use ?body=
    const smsUrl = /iPhone|iPad|iPod/i.test(navigator.userAgent) 
      ? `sms:&body=${text}`
      : `sms:?body=${text}`;
    window.open(smsUrl);
  };

  const shareViaEmail = () => {
    const text = getShareText();
    const subject = encodeURIComponent('🏆 My GiftDuel Tournament Results!');
    const body = encodeURIComponent(text + '\n\nTry GiftDuel yourself: ' + window.location.origin);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  const downloadResultsImage = async () => {
    if (!resultsRef.current) return;
    
    try {
      const canvas = await html2canvas(resultsRef.current, {
        backgroundColor: '#F8FAFC',
        scale: 2
      });
      
      const link = document.createElement('a');
      link.download = 'my-giftduel-results.png';
      link.href = canvas.toDataURL();
      link.click();
    } catch (e) {
      console.error('Error generating image:', e);
      alert('Failed to generate image. Please try again.');
    }
  };

  const shareToTwitter = () => {
    if (!tournament) return;
    const winnerGift = getGiftById(tournament.winner_id);
    const text = `I just discovered my ultimate gift choice: ${winnerGift.title}! 🎁✨ Find out yours at`;
    const url = window.location.origin;
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`, '_blank');
  };

  if (!tournament || gifts.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center catalog-bg">
        <div className="text-slate-800 text-xl" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Loading results...</div>
      </div>
    );
  }

  const winnerGift = getGiftById(tournament.winner_id);
  // Top 5: Winner + the 4 items that advanced the farthest (runner-up, semi-finalists, etc.)
  // Rankings array structure: [runner-up, semi-finalist, semi-finalist, ..., earlier losers, winner]
  const winner = tournament.rankings[tournament.rankings.length - 1]; // Last item is winner
  const topLosers = tournament.rankings.slice(0, 4); // First 4 are the ones who advanced farthest
  const topRankings = [winner, ...topLosers].map(id => getGiftById(id)).filter(Boolean);

  return (
    <div className="min-h-screen catalog-bg">
      <div className="container mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-3 mb-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_giftduel-admin/artifacts/297n31pv_image.png" 
              alt="GiftDuel" 
              className="w-12 h-12 animate-bounce"
            />
            <h1 className="text-5xl font-bold text-slate-800" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Tournament Complete!
            </h1>
          </div>
          <p className="text-xl text-slate-600 mb-6" style={{ fontFamily: 'Inter, sans-serif' }}>Here&apos;s what you really want</p>
          
          {/* Amazon Associate Disclosure */}
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 max-w-2xl mx-auto">
            <p className="text-sm text-slate-600 text-center" style={{ fontFamily: 'Inter, sans-serif' }}>
              <span className="font-semibold">As an Amazon Associate, GiftDuel earns from qualifying purchases.</span> This commission doesn&apos;t cost you anything extra — we simply receive a small referral fee when you use our links.
            </p>
          </div>
        </div>

        {/* Results Content - for screenshot */}
        <div ref={resultsRef}>
          {/* Winner Card */}
          <div className="max-w-2xl mx-auto mb-16" data-testid="winner-card">
            <div className="winner-card">
              <div className="text-center mb-4">
                <Badge className="winner-badge text-white text-lg px-6 py-2">
                  🏆 Your #1 Choice
                </Badge>
              </div>
              <div className="relative">
                <img src={winnerGift.image_url} alt={winnerGift.title} className="w-full h-80 object-cover rounded-lg" />
              </div>
              <div className="p-4 md:p-8 text-center">
                <h2 className="text-2xl md:text-4xl font-bold text-slate-800 mb-3 md:mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  {winnerGift.title}
                </h2>
                <p className="text-base md:text-xl text-slate-600 mb-4 md:mb-6" style={{ fontFamily: 'Inter, sans-serif' }}>
                  {winnerGift.description}
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-6 mb-6 md:mb-8">
                  <span className="text-2xl md:text-4xl font-bold price-text">${winnerGift.price.toFixed(2)}</span>
                  <div className="flex items-center gap-2">
                    <Star className="w-5 h-5 md:w-6 md:h-6 fill-amber-500 text-amber-500" />
                    <span className="text-xl md:text-2xl text-slate-700">{winnerGift.rating}</span>
                  </div>
                </div>
                <Button 
                  onClick={() => window.open(winnerGift.affiliate_link, '_blank')} 
                  size="lg" 
                  className="cta-button w-full sm:w-auto"
                  data-testid="buy-now-winner-btn"
                >
                  <ShoppingCart className="mr-2 w-4 h-4 md:w-5 md:h-5" /> Buy Now
                </Button>
              </div>
            </div>
          </div>

          {/* Top 5 Rankings */}
          <div className="max-w-4xl mx-auto mb-12">
            <h2 className="text-3xl font-bold text-slate-800 text-center mb-8" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Your Top {Math.min(5, topRankings.length)} Favorites
            </h2>
            <div className="space-y-4" data-testid="rankings-list">
              {topRankings.map((gift, index) => (
                <div key={gift.id} className="ranking-card" data-testid={`ranking-${index + 1}`}>
                  <div className="flex items-center gap-3 md:gap-6">
                    <div className="rank-badge flex-shrink-0">
                      {index + 1}
                    </div>
                    <img src={gift.image_url} alt={gift.title} className="w-16 h-16 md:w-24 md:h-24 object-cover rounded-lg flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm md:text-xl font-bold text-slate-800 mb-1 truncate" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                        {gift.title}
                      </h3>
                      <p className="text-xs md:text-base text-slate-600 line-clamp-2 hidden sm:block" style={{ fontFamily: 'Inter, sans-serif' }}>{gift.description}</p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <span className="text-lg md:text-2xl font-bold price-text block">${gift.price.toFixed(2)}</span>
                      <Button 
                        onClick={() => window.open(gift.affiliate_link, '_blank')} 
                        className="mt-2 select-button text-xs md:text-sm"
                        size="sm"
                        data-testid={`buy-ranking-${index + 1}`}
                      >
                        <ShoppingCart className="mr-1 w-3 h-3 md:w-4 md:h-4" /> Buy
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="text-center">
          <div className="flex flex-wrap gap-4 justify-center mb-6">
            <Button onClick={playAgain} size="lg" className="cta-button" data-testid="play-again-btn">
              Play Again
            </Button>
            
            <Button 
              onClick={shareViaWhatsApp}
              size="lg" 
              className="share-button-whatsapp"
              data-testid="share-whatsapp-btn"
            >
              <MessageCircle className="mr-2 w-5 h-5" /> Share on WhatsApp
            </Button>
            
            <Button 
              onClick={shareViaSMS}
              size="lg" 
              variant="outline" 
              className="filter-button"
              data-testid="share-sms-btn"
            >
              <MessageCircle className="mr-2 w-5 h-5" /> Share by Text
            </Button>
            
            <Button 
              onClick={shareViaEmail}
              size="lg" 
              variant="outline" 
              className="filter-button"
              data-testid="share-email-btn"
            >
              <Mail className="mr-2 w-5 h-5" /> Share by Email
            </Button>
            
            <Button 
              onClick={downloadResultsImage}
              size="lg" 
              variant="outline" 
              className="filter-button"
              data-testid="download-results-btn"
            >
              <Download className="mr-2 w-5 h-5" /> Download Image
            </Button>
          </div>
          
          <p className="text-sm text-slate-500" style={{ fontFamily: 'Inter, sans-serif' }}>
            Share your results with friends and family to help them choose the perfect gift!
          </p>
        </div>
      </div>
    </div>
  );
};

// Admin Page
const Admin = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [gifts, setGifts] = useState([]);
  const [editingGift, setEditingGift] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const navigate = useNavigate();

  // Debug log when showForm changes
  useEffect(() => {
    console.log('showForm state:', showForm);
  }, [showForm]);

  const [formData, setFormData] = useState({
    id: '',
    title: '',
    description: '',
    price: 0,
    image_url: '',
    category: 'Tech',
    rating: 4.5,
    affiliate_link: ''
  });

  const handleLogin = async () => {
    try {
      await axios.post(`${API}/admin/login`, { password });
      setIsAuthenticated(true);
      loadGifts();
    } catch (e) {
      alert('Invalid password');
    }
  };

  const loadGifts = async () => {
    try {
      const response = await axios.get(`${API}/gifts`);
      setGifts(response.data);
    } catch (e) {
      console.error('Error loading gifts:', e);
    }
  };

  const handleSave = async (e) => {
    if (e) e.preventDefault();
    
    // Validation
    if (!formData.id || !formData.title || !formData.image_url || !formData.affiliate_link) {
      alert('Please fill in all required fields: ID, Title, Image URL, and Affiliate Link');
      return;
    }
    
    if (formData.price <= 0) {
      alert('Price must be greater than 0');
      return;
    }
    
    try {
      console.log('Saving gift:', formData);
      
      if (editingGift) {
        const response = await axios.put(`${API}/admin/gifts/${formData.id}`, formData);
        console.log('Update response:', response.data);
      } else {
        const response = await axios.post(`${API}/admin/gifts`, formData);
        console.log('Create response:', response.data);
      }
      
      await loadGifts();
      resetForm();
      alert('Product saved successfully!');
    } catch (e) {
      console.error('Error saving gift:', e);
      const errorMsg = e.response?.data?.detail || e.message || 'Unknown error';
      alert('Error saving product: ' + errorMsg);
    }
  };

  const handleEdit = (gift) => {
    setEditingGift(gift);
    setFormData(gift);
    setShowForm(true);
  };

  const handleDelete = async (giftId) => {
    if (!confirm('Are you sure you want to delete this gift?')) return;
    
    try {
      await axios.delete(`${API}/admin/gifts/${giftId}`);
      loadGifts();
      alert('Gift deleted successfully!');
    } catch (e) {
      alert('Error deleting gift');
    }
  };

  const resetForm = (hideForm = true) => {
    setFormData({
      id: '',
      title: '',
      description: '',
      price: 0,
      image_url: '',
      category: 'Tech',
      rating: 4.5,
      affiliate_link: ''
    });
    setEditingGift(null);
    if (hideForm) {
      setShowForm(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center catalog-bg">
        <Card className="p-8 max-w-md w-full">
          <h1 className="text-3xl font-bold text-center mb-6" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Admin Login
          </h1>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleLogin()}
            placeholder="Enter admin password"
            className="w-full p-3 border border-slate-300 rounded-lg mb-4"
            style={{ fontFamily: 'Inter, sans-serif' }}
          />
          <Button onClick={handleLogin} className="w-full cta-button">
            Login
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen catalog-bg">
      <div className="container mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-4xl font-bold text-slate-800" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Product Management
          </h1>
          <div className="flex gap-4">
            <Button onClick={() => navigate('/')} type="button" variant="outline">
              Back to Site
            </Button>
            <Button 
              onClick={() => { 
                console.log('Add New Product clicked');
                resetForm(false);
                setShowForm(true); 
              }} 
              type="button" 
              className="cta-button"
            >
              + Add New Product
            </Button>
          </div>
        </div>

        {console.log('Rendering admin, showForm:', showForm)}
        
        {showForm && (
          <Card className="p-6 mb-8 border-4 border-teal-500">
            <h2 className="text-2xl font-bold mb-4 text-teal-600" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              {editingGift ? 'Edit Product' : 'Add New Product'}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-bold mb-2">Product ID</label>
                <input
                  type="text"
                  value={formData.id}
                  onChange={(e) => setFormData({...formData, id: e.target.value})}
                  disabled={editingGift}
                  className="w-full p-2 border rounded"
                  placeholder="unique-id"
                />
              </div>
              <div>
                <label className="block text-sm font-bold mb-2">Title</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  className="w-full p-2 border rounded"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-bold mb-2">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  className="w-full p-2 border rounded"
                  rows="3"
                />
              </div>
              <div>
                <label className="block text-sm font-bold mb-2">Price ($)</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.price}
                  onChange={(e) => setFormData({...formData, price: parseFloat(e.target.value)})}
                  className="w-full p-2 border rounded"
                />
              </div>
              <div>
                <label className="block text-sm font-bold mb-2">Rating (1-5)</label>
                <input
                  type="number"
                  step="0.1"
                  min="1"
                  max="5"
                  value={formData.rating}
                  onChange={(e) => setFormData({...formData, rating: parseFloat(e.target.value)})}
                  className="w-full p-2 border rounded"
                />
              </div>
              <div>
                <label className="block text-sm font-bold mb-2">Category</label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({...formData, category: e.target.value})}
                  className="w-full p-2 border rounded"
                >
                  <option value="Tech">Tech</option>
                  <option value="Home">Home</option>
                  <option value="Fashion">Fashion</option>
                  <option value="Fitness">Fitness</option>
                  <option value="Entertainment">Entertainment</option>
                  <option value="Food">Food</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-bold mb-2">Image URL</label>
                <input
                  type="text"
                  value={formData.image_url}
                  onChange={(e) => setFormData({...formData, image_url: e.target.value})}
                  className="w-full p-2 border rounded"
                  placeholder="https://..."
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-bold mb-2">Amazon Affiliate Link</label>
                <input
                  type="text"
                  value={formData.affiliate_link}
                  onChange={(e) => setFormData({...formData, affiliate_link: e.target.value})}
                  className="w-full p-2 border rounded"
                  placeholder="https://www.amazon.com/..."
                />
              </div>
            </div>
            <div className="flex gap-4 mt-6">
              <Button onClick={handleSave} type="button" className="cta-button">
                {editingGift ? 'Update Product' : 'Add Product'}
              </Button>
              <Button onClick={resetForm} type="button" variant="outline">
                Cancel
              </Button>
            </div>
          </Card>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {gifts.map(gift => (
            <Card key={gift.id} className="p-4">
              <img src={gift.image_url} alt={gift.title} className="w-full h-48 object-cover rounded-lg mb-3" />
              <h3 className="font-bold text-lg mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                {gift.title}
              </h3>
              <p className="text-sm text-slate-600 mb-2">{gift.description}</p>
              <div className="flex justify-between items-center mb-3">
                <span className="text-xl font-bold price-text">${gift.price.toFixed(2)}</span>
                <Badge>{gift.category}</Badge>
              </div>
              <div className="flex gap-2">
                <Button onClick={() => handleEdit(gift)} size="sm" className="flex-1">
                  Edit
                </Button>
                <Button onClick={() => handleDelete(gift.id)} size="sm" variant="outline" className="flex-1">
                  Delete
                </Button>
              </div>
            </Card>
          ))}
        </div>

        {gifts.length === 0 && (
          <div className="text-center py-12">
            <p className="text-xl text-slate-600 mb-4">No products yet</p>
            <Button onClick={() => { resetForm(false); setShowForm(true); }} className="cta-button">
              Add Your First Product
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

// Collections Browse Page
const Collections = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen catalog-bg">
      <div className="container mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold text-slate-800 mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Curated Gift Collections
            </h1>
            <p className="text-slate-600" style={{ fontFamily: 'Inter, sans-serif' }}>
              Handpicked gift ideas for every occasion
            </p>
          </div>
          <Button onClick={() => navigate('/')} variant="outline" className="border-slate-300 text-slate-700 hover:bg-slate-100">
            Back to Home
          </Button>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {COLLECTIONS.map((collection) => (
            <Card 
              key={collection.id}
              className="cursor-pointer hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
              onClick={() => navigate(`/collections/${collection.slug}`)}
            >
              <div className="p-6">
                <div className="flex items-center gap-2 mb-3">
                  <Gift className="w-6 h-6 text-teal-600" />
                  <h3 className="text-xl font-bold text-slate-800" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                    {collection.name}
                  </h3>
                </div>
                <p className="text-slate-600 mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>
                  {collection.description}
                </p>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-500">
                    {collection.productIds.length} items
                  </span>
                  <Button variant="outline" size="sm" className="filter-button">
                    Browse Collection <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
};

// Affiliate Disclosure Page
const AffiliateDisclosure = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white text-slate-900">
      <main className="max-w-3xl mx-auto px-4 py-10 text-sm leading-relaxed">
        <Button onClick={() => navigate('/')} variant="outline" className="mb-6 border-slate-300 text-slate-700 hover:bg-slate-100">
          ← Back to Home
        </Button>

        <h1 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Affiliate Disclosure</h1>
        <p className="text-xs text-slate-500 mb-6" style={{ fontFamily: 'Inter, sans-serif' }}>Last updated: December 7, 2025</p>

        <p className="mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>
          GiftDuel is supported in part through the use of affiliate links. This means that when you click on certain links
          to products or retailers and make a purchase, we may earn a commission at no additional cost to you.
        </p>

        <h2 className="text-xl font-semibold mt-6 mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Amazon Associates Program</h2>
        <p className="mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>
          GiftDuel participates in the Amazon Services LLC Associates Program, an affiliate advertising program designed to
          provide a means for sites to earn advertising fees by advertising and linking to Amazon.com and related sites.
        </p>
        <p className="mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>
          <strong>As an Amazon Associate, GiftDuel earns from qualifying purchases.</strong> This commission does not change the price you pay for any product.
        </p>

        <h2 className="text-xl font-semibold mt-6 mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Other Affiliate Programs</h2>
        <p className="mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>
          From time to time, GiftDuel may also participate in other affiliate programs with additional retailers or networks.
          In those cases, we may receive a commission if you make a purchase after clicking one of our links.
        </p>

        <h2 className="text-xl font-semibold mt-6 mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>How This Affects You</h2>
        <p className="mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>
          Affiliate commissions help support the development and maintenance of GiftDuel. They allow us to keep the Service
          available and improve features over time.
        </p>
        <p className="mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>
          Using our affiliate links does not increase the cost of your purchase. In many cases, you would pay the same amount
          whether you use our link or navigate directly to the retailer.
        </p>

        <h2 className="text-xl font-semibold mt-6 mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Our Approach to Recommendations</h2>
        <p className="mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>
          Any gift ideas or product mentions on GiftDuel are intended for informational and convenience purposes only. While
          we may use data, popularity, or other signals to surface ideas, inclusion of a product does not constitute a formal
          endorsement or guarantee.
        </p>

        <h2 className="text-xl font-semibold mt-6 mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Questions</h2>
        <p style={{ fontFamily: 'Inter, sans-serif' }}>
          If you have any questions about this Affiliate Disclosure, please contact us at:<br />
          <a href="mailto:support@giftduel.com" className="text-teal-600 hover:text-teal-700 underline">support@giftduel.com</a>
        </p>
      </main>
    </div>
  );
};

// Terms of Service Page
const TermsOfService = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white text-slate-900">
      <main className="max-w-3xl mx-auto px-4 py-10 text-sm leading-relaxed">
        <Button onClick={() => navigate('/')} variant="outline" className="mb-6 border-slate-300 text-slate-700 hover:bg-slate-100">
          ← Back to Home
        </Button>

        <h1 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Terms of Service</h1>
        <p className="text-xs text-slate-500 mb-6" style={{ fontFamily: 'Inter, sans-serif' }}>Last updated: December 7, 2025</p>

        <p className="mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>
          Please read these Terms of Service (&quot;Terms&quot;) carefully before using GiftDuel (&quot;Service&quot;), operated by GiftDuel
          (&quot;we,&quot; &quot;us,&quot; or &quot;our&quot;).
        </p>
        <p className="mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>
          By accessing or using GiftDuel, you agree to be bound by these Terms. If you do not agree to these Terms,
          you may not use the Service.
        </p>

        <h2 className="text-xl font-semibold mt-6 mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>1. Description of the Service</h2>
        <p className="mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>
          GiftDuel is a web-based tool that allows users to:
        </p>
        <ul className="list-disc list-inside mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>
          <li>Browse curated gift ideas</li>
          <li>Create and manage wishlists</li>
          <li>Run &quot;gift duels&quot; to compare and rank items</li>
          <li>Generate and share ranked gift lists with others</li>
          <li>Click on external retailer links to view or purchase products</li>
        </ul>
        <p className="mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>
          GiftDuel does not sell products directly. All purchases are made through third-party retailers.
        </p>

        <h2 className="text-xl font-semibold mt-6 mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>2. Accounts</h2>
        <p className="mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>
          GiftDuel may allow account creation or sign-in features. When you create an account, you agree to:
        </p>
        <ul className="list-disc list-inside mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>
          <li>Provide accurate and complete information</li>
          <li>Maintain the security of your login credentials</li>
          <li>Accept responsibility for all activities that occur under your account</li>
        </ul>
        <p className="mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>
          We reserve the right to suspend or terminate accounts for any reason, including violation of these Terms,
          suspected abuse, or security concerns.
        </p>

        <h2 className="text-xl font-semibold mt-6 mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>3. User Responsibilities</h2>
        <p className="mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>
          You agree not to:
        </p>
        <ul className="list-disc list-inside mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>
          <li>Use the Service for any unlawful purpose</li>
          <li>Interfere with or disrupt the Service or servers</li>
          <li>Attempt to reverse-engineer, decompile, or obtain the source code of the Service</li>
          <li>Use automated tools (such as bots or scrapers) to access the Service without our permission</li>
          <li>Post or share content that is harmful, abusive, or otherwise inappropriate (if user-generated content features are enabled)</li>
        </ul>

        <h2 className="text-xl font-semibold mt-6 mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>4. Affiliate Relationships</h2>
        <p className="mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>
          GiftDuel participates in affiliate marketing programs, which means we may earn commissions from qualifying purchases
          made through links on the Service.
        </p>
        <p className="mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>
          <strong>As an Amazon Associate, GiftDuel earns from qualifying purchases.</strong> Purchases are completed
          on third-party retailer sites. Your price is not affected by our commissions.
        </p>
        <p className="mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>
          We are not responsible for:
        </p>
        <ul className="list-disc list-inside mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>
          <li>Product availability, pricing, quality, or descriptions on third-party sites</li>
          <li>Issues related to shipping, returns, or customer service</li>
          <li>Accuracy or completeness of information on third-party websites</li>
        </ul>

        <h2 className="text-xl font-semibold mt-6 mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>5. Intellectual Property</h2>
        <p className="mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>
          All content on GiftDuel, including but not limited to text, branding, logos, design, and interactive features,
          is owned by or licensed to GiftDuel and is protected by applicable intellectual property laws.
        </p>
        <p className="mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>
          You may not copy, modify, distribute, or create derivative works based on the Service or its content without our
          prior written consent, except as allowed by applicable law.
        </p>

        <h2 className="text-xl font-semibold mt-6 mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>6. Third-Party Links</h2>
        <p className="mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>
          The Service contains links to third-party websites and retailers. These links are provided for convenience only.
          We do not control and are not responsible for:
        </p>
        <ul className="list-disc list-inside mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>
          <li>The content of third-party websites</li>
          <li>Privacy practices and policies of third parties</li>
          <li>Products, services, or transactions conducted on third-party sites</li>
        </ul>
        <p className="mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>
          Your interactions with third-party websites are solely between you and those third parties.
        </p>

        <h2 className="text-xl font-semibold mt-6 mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>7. Disclaimer of Warranties</h2>
        <p className="mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>
          The Service is provided on an <strong>&quot;as is&quot;</strong> and <strong>&quot;as available&quot;</strong> basis, without any warranty
          of any kind, express or implied. We do not warrant that:
        </p>
        <ul className="list-disc list-inside mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>
          <li>The Service will be uninterrupted, secure, or error-free</li>
          <li>Information on the Service will be accurate, complete, or current</li>
          <li>Third-party sites will function as expected</li>
        </ul>

        <h2 className="text-xl font-semibold mt-6 mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>8. Limitation of Liability</h2>
        <p className="mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>
          To the fullest extent permitted by law, GiftDuel and its owners, employees, and affiliates shall not be liable for
          any indirect, incidental, special, consequential, or punitive damages, including but not limited to loss of profits,
          data, use, goodwill, or other intangible losses, arising out of or related to your use of the Service.
        </p>
        <p className="mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>
          In no event shall our total liability exceed the amount you have paid to us (if any) for use of the Service in the
          twelve (12) months preceding the claim.
        </p>

        <h2 className="text-xl font-semibold mt-6 mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>9. Termination</h2>
        <p className="mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>
          We may suspend or terminate your access to the Service at any time, with or without notice, for any reason,
          including violation of these Terms or suspected abuse.
        </p>

        <h2 className="text-xl font-semibold mt-6 mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>10. Changes to These Terms</h2>
        <p className="mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>
          We may update these Terms from time to time. When we do, we will update the &quot;Last updated&quot; date at the top of this page.
          Your continued use of the Service after changes are posted constitutes your acceptance of the updated Terms.
        </p>

        <h2 className="text-xl font-semibold mt-6 mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>11. Governing Law</h2>
        <p className="mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>
          These Terms are governed by the laws of the State of Washington, USA, without regard to its conflict of law principles.
          You agree to submit to the personal jurisdiction of the courts located in Washington for any disputes arising out of
          or relating to these Terms or your use of the Service.
        </p>

        <h2 className="text-xl font-semibold mt-6 mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>12. Contact Information</h2>
        <p style={{ fontFamily: 'Inter, sans-serif' }}>
          If you have any questions about these Terms, please contact us at:<br />
          <a href="mailto:support@giftduel.com" className="text-teal-600 hover:text-teal-700 underline">support@giftduel.com</a>
        </p>
      </main>
    </div>
  );
};

// Privacy Policy Page
const PrivacyPolicy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white text-slate-900">
      <main className="max-w-3xl mx-auto px-4 py-10 text-sm leading-relaxed">
        <Button onClick={() => navigate('/')} variant="outline" className="mb-6 border-slate-300 text-slate-700 hover:bg-slate-100">
          ← Back to Home
        </Button>

        <h1 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Privacy Policy</h1>
        <p className="text-xs text-slate-500 mb-6" style={{ fontFamily: 'Inter, sans-serif' }}>Last updated: December 7, 2025</p>

        <p className="mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>
          GiftDuel (&quot;we,&quot; &quot;our,&quot; or &quot;us&quot;) operates the website <strong>giftduel.com</strong> and related
          services (&quot;Service&quot;). This Privacy Policy explains how we collect, use, and protect your information
          when you use GiftDuel.
        </p>
        <p className="mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>
          By using GiftDuel, you agree to the practices described in this Privacy Policy.
        </p>

        <h2 className="text-xl font-semibold mt-6 mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>1. Information We Collect</h2>

        <h3 className="font-semibold mt-4 mb-1" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>A. Information you provide directly</h3>
        <p className="mb-3" style={{ fontFamily: 'Inter, sans-serif' }}>
          We may collect information you provide directly to us, such as:
        </p>
        <ul className="list-disc list-inside mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>
          <li>Wishlist items you select</li>
          <li>Gift preferences and choices during duels</li>
          <li>Optional account information (such as an email address, if account creation is added in the future)</li>
        </ul>

        <h3 className="font-semibold mt-4 mb-1" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>B. Automatically collected information</h3>
        <p className="mb-3" style={{ fontFamily: 'Inter, sans-serif' }}>
          When you visit GiftDuel, we automatically collect certain information, such as:
        </p>
        <ul className="list-disc list-inside mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>
          <li>IP address</li>
          <li>Browser type</li>
          <li>Device information</li>
          <li>Pages visited and general usage analytics</li>
        </ul>
        <p className="mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>
          We use this information to operate, maintain, and improve the Service.
        </p>

        <h3 className="font-semibold mt-4 mb-1" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>C. Affiliate tracking information</h3>
        <p className="mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>
          When you click an affiliate link (for example, a link to Amazon), the retailer may track visits and
          purchases for commission purposes. GiftDuel does not receive personal information about your purchase.
          We only receive aggregated commission and performance reports.
        </p>

        <h2 className="text-xl font-semibold mt-6 mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>2. How We Use Your Information</h2>
        <p className="mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>
          We use the information we collect to:
        </p>
        <ul className="list-disc list-inside mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>
          <li>Operate and improve GiftDuel</li>
          <li>Personalize and enhance your experience</li>
          <li>Generate duel results and ranked gift lists</li>
          <li>Provide customer support</li>
          <li>Monitor usage and performance for analytics</li>
          <li>Comply with legal obligations</li>
        </ul>
        <p className="mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>
          We do <strong>not</strong> sell your personal information.
        </p>

        <h2 className="text-xl font-semibold mt-6 mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>3. Affiliate Disclosure</h2>
        <p className="mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>
          GiftDuel uses affiliate links to third-party retailers.
          <strong> As an Amazon Associate, GiftDuel earns from qualifying purchases.</strong> Using our links does not increase the price you pay.
        </p>

        <h2 className="text-xl font-semibold mt-6 mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>4. Sharing Your Information</h2>
        <p className="mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>
          We do <strong>not</strong> sell user data. We may share limited information only with:
        </p>
        <ul className="list-disc list-inside mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>
          <li>Service providers that help us operate the Service (such as hosting and analytics providers)</li>
          <li>Retailers you visit via affiliate links, who may track visits and purchases for commission reporting</li>
          <li>Law enforcement or authorities if required by law</li>
        </ul>
        <p className="mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>
          We do not share personally identifiable information with third parties for their own independent marketing purposes.
        </p>

        <h2 className="text-xl font-semibold mt-6 mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>5. Cookies &amp; Tracking Technologies</h2>
        <p className="mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>
          GiftDuel uses cookies and similar technologies to:
        </p>
        <ul className="list-disc list-inside mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>
          <li>Analyze usage and performance</li>
          <li>Improve features and user experience</li>
          <li>Maintain sessions or preferences (if applicable)</li>
        </ul>
        <p className="mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>
          You can control cookies through your browser settings. Disabling cookies may affect certain features of the Service.
        </p>

        <h2 className="text-xl font-semibold mt-6 mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>6. Children&apos;s Privacy</h2>
        <p className="mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>
          GiftDuel is designed for use by adults. Children under 13 may use GiftDuel only under the supervision
          of a parent or guardian and through an adult&apos;s account.
        </p>
        <p className="mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>
          We do not knowingly:
        </p>
        <ul className="list-disc list-inside mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>
          <li>Collect personal information directly from children under 13</li>
          <li>Allow children to create their own accounts</li>
          <li>Use tools specifically to track or profile children</li>
        </ul>
        <p className="mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>
          If we learn that we have collected personal information directly from a child under 13, we will delete that information.
        </p>

        <h2 className="text-xl font-semibold mt-6 mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>7. Third-Party Links</h2>
        <p className="mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>
          GiftDuel contains links to third-party websites, including retailers such as Amazon. We are not
          responsible for the content, privacy practices, or policies of those websites. You should review
          their privacy policies before providing any information or making purchases.
        </p>

        <h2 className="text-xl font-semibold mt-6 mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>8. Security</h2>
        <p className="mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>
          We use reasonable technical and organizational measures to protect your information, including HTTPS
          encryption and secure hosting. However, no method of transmission or storage is completely secure,
          and we cannot guarantee absolute security.
        </p>

        <h2 className="text-xl font-semibold mt-6 mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>9. Data Retention</h2>
        <p className="mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>
          We retain information only as long as necessary to:
        </p>
        <ul className="list-disc list-inside mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>
          <li>Provide and improve the Service</li>
          <li>Comply with legal obligations</li>
          <li>Resolve disputes or enforce our agreements</li>
        </ul>
        <p className="mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>
          You may contact us to request deletion of certain information, subject to legal or operational limitations.
        </p>

        <h2 className="text-xl font-semibold mt-6 mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>10. Your Rights</h2>
        <p className="mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>
          Depending on your location, you may have the right to access, update, or request deletion of your personal information.
          To exercise these rights, please contact us at the email address below.
        </p>

        <h2 className="text-xl font-semibold mt-6 mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>11. Changes to This Privacy Policy</h2>
        <p className="mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>
          We may update this Privacy Policy from time to time. When we do, we will update the &quot;Last updated&quot;
          date at the top of this page. Changes are effective when posted.
        </p>

        <h2 className="text-xl font-semibold mt-6 mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>12. Contact Us</h2>
        <p style={{ fontFamily: 'Inter, sans-serif' }}>
          If you have any questions about this Privacy Policy, please contact us at:<br />
          <a href="mailto:support@giftduel.com" className="text-teal-600 hover:text-teal-700 underline">support@giftduel.com</a>
        </p>
      </main>
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/catalog" element={<Catalog />} />
          <Route path="/collections" element={<Collections />} />
          <Route path="/collections/:slug" element={<CollectionDetail />} />
          <Route path="/my-list" element={<MyList />} />
          <Route path="/bracket" element={<Bracket />} />
          <Route path="/results" element={<Results />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/affiliate" element={<AffiliateDisclosure />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
